import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  WalletIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  StarIcon,
} from '@heroicons/react/24/outline'
import { dashboardAPI, budgetAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import useTheme from '../hooks/useTheme'
import useLanguage from '../hooks/useLanguage'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import Button from '../components/ui/Button'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { CashflowChart, CategoryPieChart, ActivityHeatmap } from '../components/charts'
import { formatNumber, formatDate } from '../utils/formatters'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
}

const buildCashflowData = (trend, period = 'monthly', locale = 'pl-PL') => {
  if (!trend || trend.length === 0) {
    const now = new Date()
    const count = period === 'weekly' ? 7 : period === 'yearly' ? 12 : 6
    return Array.from({ length: count }, (_, i) => {
      const d = period === 'weekly'
        ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - (count - 1 - i))
        : new Date(now.getFullYear(), now.getMonth() - (count - 1 - i), 1)
      const label = period === 'weekly'
        ? d.toLocaleDateString(locale, { weekday: 'short' })
        : d.toLocaleDateString(locale, { month: 'short', ...(period === 'yearly' ? { year: '2-digit' } : {}) })
      return { label, income: 0, expenses: 0 }
    })
  }

  return trend.map((item) => {
    const dateStr = item.month || item.label || ''
    let label = dateStr
    try {
      if (period === 'weekly') {
        // YYYY-MM-DD → nazwa dnia tygodnia
        label = new Date(dateStr + 'T12:00:00').toLocaleDateString(locale, { weekday: 'short' })
      } else if (period === 'yearly') {
        // YYYY-MM → skrócona nazwa miesiąca + rok
        label = new Date(dateStr + '-01T12:00:00').toLocaleDateString(locale, { month: 'short', year: '2-digit' })
      } else {
        // YYYY-MM → skrócona nazwa miesiąca
        label = new Date(dateStr + '-01T12:00:00').toLocaleDateString(locale, { month: 'short' })
      }
    } catch (_) {}

    return {
      label,
      income: parseFloat(item.income || 0),
      expenses: parseFloat(item.total || 0),
    }
  })
}

const DashboardPage = () => {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const { t } = useTranslation('dashboard')
  const { locale } = useLanguage()
  const [cashflowPeriod, setCashflowPeriod] = useState('monthly')

  const { data: dashboardData, isLoading, error } = useQuery(
    'dashboard',
    dashboardAPI.getDashboard,
    { refetchInterval: 30000 }
  )

  const { data: expensesResponse } = useQuery(
    'recent-expenses',
    () => budgetAPI.getExpenses({ limit: 10, sort_by: 'expense_date', sort_order: 'desc' }),
    { refetchInterval: 30000 }
  )

  const recentExpenses = expensesResponse?.data?.expenses || expensesResponse?.expenses || []

  const { data: goalsResponse } = useQuery(
    'dashboard-goals',
    () => budgetAPI.getGoals({ status: 'active' }),
    { refetchInterval: 30000 }
  )

  const activeGoals = goalsResponse?.data?.goals || []

  const { data: cashflowResponse } = useQuery(
    ['dashboard-cashflow', cashflowPeriod],
    () => dashboardAPI.getCashflow({ period: cashflowPeriod }),
    { refetchInterval: 60000 }
  )

  const { data: activityResponse } = useQuery(
    'dashboard-activity',
    () => dashboardAPI.getActivity({ weeks: 12 }),
    { refetchInterval: 30000 }
  )

  const activityData = activityResponse?.data || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text={t('loading')} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p style={{ color: '#e55353', fontWeight: 700 }}>{t('error')}</p>
      </div>
    )
  }

  const { stats, top_categories, budget_alerts, monthly_trend } = dashboardData?.data || {}

  const expensesThisMonth = stats?.expenses?.this_month || 0
  const expensesLastMonth = stats?.expenses?.last_month || 0
  const expenseChange = expensesLastMonth > 0
    ? Math.round((expensesThisMonth - expensesLastMonth) / expensesLastMonth * 100)
    : 0

  const incomeThisMonth = stats?.income?.this_month || 0
  const incomeLastMonth = stats?.income?.last_month || 0
  const incomeChange = incomeLastMonth > 0
    ? Math.round((incomeThisMonth - incomeLastMonth) / incomeLastMonth * 100)
    : 0
  const balanceTotal = incomeThisMonth - expensesThisMonth
  const savingsTotal = incomeThisMonth > expensesThisMonth ? incomeThisMonth - expensesThisMonth : 0
  const savingsPercent = incomeThisMonth > 0 ? ((incomeThisMonth - expensesThisMonth) / incomeThisMonth * 100) : 0

  const cashflowData = buildCashflowData(cashflowResponse?.data, cashflowPeriod, locale)

  const alerts = []
  if (budget_alerts) {
    budget_alerts.forEach((alert) => {
      if (alert.spent_percentage >= 80) {
        alerts.push({
          id: `budget-${alert.id}`,
          type: alert.spent_percentage > 100 ? 'error' : 'warning',
          text: alert.spent_percentage > 100
            ? t('budgetExceeded', { name: alert.name })
            : t('budgetPercent', { name: alert.name, percentage: Math.round(alert.spent_percentage) }),
        })
      }
    })
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Row 1: 3-column stat cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {/* Obecne Saldo */}
        <Card>
          <Card.Header>
            <div>
              <div className="card-title">{t('balance')}</div>
              <div className="card-sub">{t('allAccounts')}</div>
            </div>
            <div className="value">{formatNumber(balanceTotal, locale)} {t('common:currency')}</div>
          </Card.Header>

          <div className="divider" />

          <div className="inline-row">
            <div>
              <div className="label-muted">{t('income')}</div>
              <div className="text-strong mt-1.5">{formatNumber(incomeThisMonth, locale)} {t('common:currency')}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="label-muted">{t('expenses')}</div>
              <div className="text-strong mt-1.5">{formatNumber(expensesThisMonth, locale)} {t('common:currency')}</div>
            </div>
          </div>

          <div className="divider" />
          <div className="label-muted">{t('balanceDescription')}</div>
        </Card>

        {/* Przychody */}
        <Card>
          <Card.Header>
            <div>
              <div className="card-title">{t('incomeTitle')}</div>
              <div className="card-sub">{t('thisMonth')}</div>
            </div>
            <div className="value">{formatNumber(incomeThisMonth, locale)} {t('common:currency')}</div>
          </Card.Header>

          <div className="divider" />

          <div className="inline-row">
            <div>
              <div className="label-muted">{t('previousMonth')}</div>
              <div className="text-strong mt-1.5">{formatNumber(incomeLastMonth, locale)} {t('common:currency')}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="label-muted">{t('change')}</div>
              <div className="mt-1.5">
                <span className="trend-badge">
                  <span style={{ color: incomeChange >= 0 ? '#1dbf73' : '#e55353' }}>
                    {incomeChange >= 0 ? '▲' : '▼'}
                  </span>
                  {Math.abs(incomeChange)}%
                </span>
              </div>
            </div>
          </div>

          <div className="divider" />
          <div className="label-muted">{t('incomeDescription')}</div>
        </Card>

        {/* Cele */}
        <Card>
          <Card.Header>
            <div>
              <div className="card-title">{t('goals')}</div>
              <div className="card-sub">{t('thisMonth')}</div>
            </div>
            <Badge variant="primary" size="sm">
              <Link to="/dashboard/budget/goals" className="no-underline" style={{ color: 'inherit' }}>
                {t('common:viewAll')}
              </Link>
            </Badge>
          </Card.Header>

          <div className="divider" />

          {activeGoals.length > 0 ? (
            <>
              {activeGoals.slice(0, 1).map((goal) => {
                const target = parseFloat(goal.target_amount || 0)
                const current = parseFloat(goal.current_amount || 0)
                const progress = target > 0 ? Math.min((current / target) * 100, 100) : 0

                return (
                  <div key={goal.id}>
                    <div className="inline-row">
                      <div>
                        <div className="label-muted">{goal.title}</div>
                        <div className="text-strong mt-1.5">
                          {formatNumber(current, locale)} / {formatNumber(target, locale)} {t('common:currency')}
                        </div>
                      </div>
                      <Badge variant={progress >= 50 ? 'primary' : 'secondary'} size="sm">
                        {progress >= 75 ? t('onTrack') : t('inProgress')}
                      </Badge>
                    </div>

                    <div className="mt-2.5">
                      <div className="progress-bar">
                        <span className="progress-bar-fill" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </>
          ) : (
            <div className="text-center py-4">
              <div className="label-muted">{t('noActiveGoals')}</div>
            </div>
          )}

          <div className="divider" />
          <div className="inline-row">
            <span className="label-muted">{t('savings')}</span>
            <span className="text-strong" style={{ color: '#167e75' }}>{formatNumber(savingsTotal, locale)} {t('common:currency')}</span>
          </div>
        </Card>
      </motion.div>

      {/* Row 2: Transactions + Stats/Expenses */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-[1.3fr_1.6fr] gap-4 mb-4">
        {/* Recent Transactions */}
        <Card>
          <Card.Header>
            <div>
              <div className="card-title">{t('recentTransactions')}</div>
              <div className="card-sub">{t('all')}</div>
            </div>
            <Badge variant="secondary" size="sm">
              <Link to="/dashboard/budget/expenses" className="no-underline" style={{ color: 'inherit' }}>
                {t('common:viewAll')}
              </Link>
            </Badge>
          </Card.Header>

          <div className="divider" />

          <div className="tx-list">
            {recentExpenses.length > 0 ? (
              recentExpenses.slice(0, 5).map((expense) => (
                <TransactionRow key={expense.id} transaction={expense} />
              ))
            ) : (
              <div className="text-center py-6">
                <BanknotesIcon className="mx-auto h-8 w-8" style={{ color: 'var(--color-icon-faint)' }} />
                <p className="mt-2 text-sm" style={{ color: 'var(--color-text-faint)' }}>{t('noTransactions')}</p>
              </div>
            )}
          </div>

          {alerts.length > 0 && (
            <>
              <div className="divider" />
              {alerts.slice(0, 1).map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center gap-2 p-2 rounded-xl text-xs font-bold"
                  style={{
                    background: alert.type === 'error' ? 'rgba(229,83,83,.10)' : 'rgba(255,176,32,.10)',
                    color: alert.type === 'error' ? '#e55353' : '#b45309',
                  }}
                >
                  <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{alert.text}</span>
                </div>
              ))}
            </>
          )}
        </Card>

        {/* Right column: Charts stacked */}
        <div className="flex flex-col gap-4">
          {/* Cashflow Chart */}
          <Card>
            <Card.Header>
              <div>
                <div className="card-title">{t('statistics')}</div>
                <div className="card-sub">{t('incomeVsExpenses')}</div>
              </div>
              <div
                className="flex overflow-hidden"
                style={{
                  borderRadius: '14px',
                  border: '1px solid var(--color-border)',
                  boxShadow: 'var(--shadow-btn)',
                  background: 'var(--color-glass)',
                }}
              >
                {['weekly', 'monthly', 'yearly'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setCashflowPeriod(p)}
                    className="px-3 py-2 text-[12px] font-black transition-colors cursor-pointer"
                    style={{
                      background: cashflowPeriod === p ? 'rgba(31,159,147,.16)' : 'transparent',
                      color: cashflowPeriod === p ? '#167e75' : 'var(--color-text-muted)',
                      border: 'none',
                    }}
                  >
                    {p === 'weekly' ? t('week') : p === 'monthly' ? t('month') : t('year')}
                  </button>
                ))}
              </div>
            </Card.Header>

            <div className="divider" />

            <CashflowChart
              data={cashflowData}
              isDark={isDark}
              height={220}
            />
          </Card>

          {/* Expenses Breakdown */}
          <Card>
            <Card.Header>
              <div>
                <div className="card-title">{t('expensesByCategory')}</div>
                <div className="card-sub">{t('byCategory')}</div>
              </div>
              <Badge variant="secondary" size="sm">{t('thisMonth')}</Badge>
            </Card.Header>

            <div className="divider" />

            {top_categories && top_categories.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {top_categories.slice(0, 3).map((cat, i) => {
                  const prevAmount = cat.previous_amount || 0
                  const change = prevAmount > 0
                    ? Math.round((cat.total - prevAmount) / prevAmount * 100)
                    : 0

                  return (
                    <div key={cat.name || i} style={{ padding: '10px 0', borderBottom: i < 2 ? '1px solid rgba(29,38,45,.10)' : 'none' }}>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="label-muted">{cat.name}</div>
                          <div className="text-strong">{formatNumber(cat.total, locale)} {t('common:currency')}</div>
                        </div>
                        <span className="trend-badge">
                          <span style={{ color: change > 0 ? '#1dbf73' : change < 0 ? '#e55353' : '#ffb020' }}>
                            {change > 0 ? '▲' : change < 0 ? '▼' : '●'}
                          </span>
                          {Math.abs(change)}%
                        </span>
                      </div>
                      <div className="label-muted mt-1">{t('relativeToPrevMonth')}</div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <ChartBarIcon className="mx-auto h-8 w-8" style={{ color: 'var(--color-icon-faint)' }} />
                <p className="mt-2 text-sm" style={{ color: 'var(--color-text-faint)' }}>{t('noExpenses')}</p>
              </div>
            )}
          </Card>
        </div>
      </motion.div>

      {/* Row 3: Activity + Category Pie + Budgets */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Activity Heatmap */}
        <Card>
          <Card.Header>
            <div>
              <div className="card-title">{t('activity')}</div>
              <div className="card-sub">{t('last12Weeks')}</div>
            </div>
          </Card.Header>
          <div className="divider" />
          <ActivityHeatmap
            data={activityData}
            weeks={12}
            isDark={isDark}
          />
        </Card>

        {/* Category Pie Chart */}
        <Card>
          <Card.Header>
            <div>
              <div className="card-title">{t('categories')}</div>
              <div className="card-sub">{t('expenseBreakdown')}</div>
            </div>
            <Badge variant="secondary" size="sm">
              <Link to="/dashboard/budget/reports" className="no-underline" style={{ color: 'inherit' }}>
                {t('report')}
              </Link>
            </Badge>
          </Card.Header>
          <div className="divider" />
          {top_categories && top_categories.length > 0 ? (
            <CategoryPieChart
              data={top_categories}
              isDark={isDark}
              height={220}
            />
          ) : (
            <div className="text-center py-8">
              <ChartBarIcon className="mx-auto h-8 w-8" style={{ color: 'var(--color-icon-faint)' }} />
              <p className="mt-2 text-sm" style={{ color: 'var(--color-text-faint)' }}>{t('common:noData')}</p>
            </div>
          )}
        </Card>

        {/* Budgets */}
        <Card>
          <Card.Header>
            <div>
              <div className="card-title">{t('budgets')}</div>
              <div className="card-sub">{t('thisMonth')}</div>
            </div>
            <Badge variant="secondary" size="sm">
              <Link to="/dashboard/budget/budgets" className="no-underline" style={{ color: 'inherit' }}>
                {t('common:viewAll')}
              </Link>
            </Badge>
          </Card.Header>

          <div className="divider" />

          {budget_alerts && budget_alerts.length > 0 ? (
            <div>
              {budget_alerts.map((alert, i) => {
                const spent = alert.spent || 0
                const limit = alert.amount || alert.limit || 0
                const percentage = alert.spent_percentage || 0

                return (
                  <div key={alert.id}>
                    <div className="inline-row">
                      <span className="label-muted">{alert.name}</span>
                      <span className="text-strong">{formatNumber(spent, locale)} / {formatNumber(limit, locale)} {t('common:currency')}</span>
                    </div>
                    <div className="mt-2">
                      <div className="progress-bar">
                        <span
                          className="progress-bar-fill"
                          style={{
                            width: `${Math.min(percentage, 100)}%`,
                            background: percentage > 100
                              ? '#e55353'
                              : percentage >= 80
                                ? '#ffb020'
                                : undefined,
                          }}
                        />
                      </div>
                    </div>
                    {i < budget_alerts.length - 1 && <div className="divider" />}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <ChartBarIcon className="mx-auto h-8 w-8" style={{ color: 'var(--color-icon-faint)' }} />
              <p className="mt-2 text-sm" style={{ color: 'var(--color-text-faint)' }}>{t('noActiveBudgets')}</p>
              <Button as={Link} to="/dashboard/budget/budgets" variant="ghost" size="sm" className="mt-2">
                {t('createBudget')}
              </Button>
            </div>
          )}
        </Card>
      </motion.div>
    </motion.div>
  )
}

// Sub-components

const TransactionRow = ({ transaction }) => {
  const { t } = useTranslation('dashboard')
  const { locale } = useLanguage()

  const amount = parseFloat(transaction.amount || 0)
  const categoryName = transaction.category?.name || transaction.Category?.name || ''
  const dateStr = formatDate(
    transaction.expense_date || transaction.date || transaction.createdAt,
    locale,
    { day: 'numeric', month: 'short', year: 'numeric' }
  )

  return (
    <div className="tx-item">
      <div className="flex items-center gap-3 min-w-0">
        <div className="tx-icon">
          <ArrowTrendingDownIcon className="h-4 w-4" style={{ color: 'var(--color-icon-muted)' }} />
        </div>
        <div className="min-w-0">
          <strong className="block font-black text-[14px] truncate" style={{ maxWidth: '240px', color: 'var(--color-text)' }}>
            {transaction.title || transaction.description || t('expense')}
          </strong>
          <span className="block mt-0.5 text-[12px] font-bold truncate" style={{ maxWidth: '240px', color: 'var(--color-text-muted)' }}>
            {categoryName}
          </span>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <strong className="block font-black text-[14px]" style={{ color: 'var(--color-text)' }}>
          {amount.toFixed(2)} {t('common:currency')}
        </strong>
        <span className="block mt-0.5 text-[12px] font-bold" style={{ color: 'var(--color-text-muted)' }}>
          {dateStr}
        </span>
      </div>
    </div>
  )
}

export default DashboardPage
